import { Box } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DenyButton from "../../common/buttons/DenyButton";
import AcceptButton from "../../common/buttons/AcceptButton";
import { MedicalIndicatorsEnum } from "../../../models/enums/medicalIndicatorsEnum";
import { MedicalCheckupDto } from "../../../models/medicalCheckups/medicalCheckupDto";
import { MedicalCheckupResult } from "../../../models/enums/medicalCheckupResult";
import { MedicalCheckupVerdictDto } from "../../../models/medicalCheckups/medicalCheckupVerdictDto";
import { medicalCheckupSigningService, ValidationError, WorkplaceError, NetworkError } from "../../../services/medicalCheckupSigningService";
import { NotAllowedDialog } from "../NotAllowedDialog";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { CertificateSelectionDialog } from "../../common/CertificateSelectionDialog";
import type { Certificate } from "../../../models/digitalSignatures/cryptoProTypes";
import { MedicDigitalSignDto } from "../../../models/medics/medicDigitalSignDto";
import { ROUTES } from "../../../constants/routes";
import { summarizeErrorMessages } from "../../../helpers/errorUtils";
import { WorkplaceMedicalCheckupDocumentContentDto } from "../../../models/medicalCheckupDocuments/workplaceSignatureDtos";
import {
    CREATE_DOCUMENTS_ERROR,
    PREPARE_DOCUMENTS_ERROR,
    SIGN_DOCUMENTS_ERROR,
    SIGNING_PROGRESS_TITLES,
    SigningProgressItem,
    SigningProgressItemKey,
    SigningProgressItemStatus,
    SigningProgressState,
    VERDICT_COMPLETED_MESSAGE,
    WORKPLACE_DOCUMENTS_HINT,
} from "./SigningProgressDialog";

interface PhysicalTokenSigningButtonsProps {
    medicalCheckup: MedicalCheckupDto;
    disabled?: boolean;
    defaultCertificate: MedicDigitalSignDto | null;
    onValidationError?: (message: string) => void;
    onWorkplaceError?: (message: string, retry?: () => Promise<void>) => void;
    onNetworkError?: (message: string) => void;
    onLoadingChange?: (loading: boolean, spinnerText?: string) => void;
    onSigningProgressChange?: (state: SigningProgressState | null) => void;
}

const progressItem = (key: SigningProgressItemKey, status: SigningProgressItemStatus, error?: string): SigningProgressItem => ({
    key,
    title: SIGNING_PROGRESS_TITLES[key],
    status,
    error,
});

const getErrorMessage = (e: any): string | undefined => e?.response?.data?.message ?? e?.message;

/**
 * Подсказка под пунктами. Если текст ошибки уже отсылает к реестру, второй раз о нём не пишем.
 */
const getDocumentsHint = (items: SigningProgressItem[]) =>
    items.some(x => x.error?.includes("«Направления на подпись»")) ? VERDICT_COMPLETED_MESSAGE : WORKPLACE_DOCUMENTS_HINT;

export const PhysicalTokenSigningButtons: React.FC<PhysicalTokenSigningButtonsProps> = ({
    medicalCheckup,
    disabled = false,
    defaultCertificate,
    onValidationError,
    onWorkplaceError,
    onNetworkError,
    onLoadingChange,
    onSigningProgressChange,
}) => {
    const [openNotAllowedDialog, setOpenNotAllowedDialog] = useState(false);
    const [openAllowConfirmationDialog, setOpenAllowConfirmationDialog] = useState(false);
    const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
    const [pendingVerdictData, setPendingVerdictData] = useState<MedicalCheckupVerdictDto | null>(null);
    const navigate = useNavigate();

    const hasRedValuesOrComplaints = () => {
        if (!medicalCheckup) return false;

        // Check for complaints
        if (medicalCheckup.complaints && medicalCheckup.complaints.length > 0) {
            return true;
        }

        // Check for red medical indicators
        const indicators = medicalCheckup.indicators;
        return (
            indicators.alcoholParam === MedicalIndicatorsEnum.High ||
            indicators.upperPressureParam === MedicalIndicatorsEnum.High ||
            indicators.lowerPressureParam === MedicalIndicatorsEnum.High ||
            indicators.pulseParam === MedicalIndicatorsEnum.High ||
            indicators.temperatureParam === MedicalIndicatorsEnum.High
        );
    };

    const performSign = async (
        verdictData: MedicalCheckupVerdictDto,
        thumbprint: string,
        certificate: MedicDigitalSignDto | null
    ) => {
        // Пункт «Подписание медосмотра» — всегда. Пока он выполняется, окно не закрывается.
        onSigningProgressChange?.({
            items: [progressItem(SigningProgressItemKey.Verdict, SigningProgressItemStatus.InProgress)],
            closable: false,
        });

        try {
            const verdictResult = await medicalCheckupSigningService.signWithPhysicalToken(
                medicalCheckup.id,
                verdictData,
                thumbprint,
                certificate
            );

            // Осмотр завершён. Справки подписываются тем же сертификатом, которым подписан осмотр.
            await signDocuments(verdictResult.documentIds, verdictResult.documentsError, thumbprint);
        } catch (e: any) {
            console.error(e);
            onSigningProgressChange?.(null);

            const retry = () => performSign(verdictData, thumbprint, certificate);

            if (e instanceof ValidationError) {
                onValidationError?.(e.message);
            } else if (e instanceof NetworkError) {
                onNetworkError?.(e.message);
            } else if (e instanceof WorkplaceError) {
                onWorkplaceError?.(e.message, retry);
            } else {
                if (e.response) {
                    const errorMessage = e.response?.data?.message ?? e.message ?? "Ошибка валидации";
                    onValidationError?.(errorMessage);
                } else {
                    const errorMessage = e.message ?? "Непредвиденная ошибка";
                    onWorkplaceError?.(errorMessage, retry);
                }
            }

            onLoadingChange?.(false);
        }
    };

    /**
     * Пункты «Формирование направлений к врачу» и «Подписание направлений к врачу».
     * Ошибки здесь осмотр не отменяют: под упавшим пунктом — одна понятная строка, технические детали — в консоль.
     * «Повторить» заново собирает и подписывает только те справки, которые не подписались (без повторного вердикта).
     */
    const signDocuments = async (documentIds: number[], documentsError: string | null | undefined, thumbprint: string) => {
        const verdictDone = progressItem(SigningProgressItemKey.Verdict, SigningProgressItemStatus.Done);

        if (documentsError) {
            // Справки не созданы: повторять нечего, в реестре их тоже нет.
            console.error("Ошибка создания направлений к врачу:", documentsError);
            onSigningProgressChange?.({
                items: [
                    verdictDone,
                    progressItem(
                        SigningProgressItemKey.PrepareDocuments,
                        SigningProgressItemStatus.Error,
                        summarizeErrorMessages([documentsError], CREATE_DOCUMENTS_ERROR)
                    ),
                    progressItem(SigningProgressItemKey.SignDocuments, SigningProgressItemStatus.Pending),
                ],
                message: VERDICT_COMPLETED_MESSAGE,
                closable: true,
            });
            return;
        }

        if (documentIds.length === 0) {
            onSigningProgressChange?.({ items: [verdictDone], closable: false });
            finish();
            return;
        }

        let prepareItem = progressItem(SigningProgressItemKey.PrepareDocuments, SigningProgressItemStatus.InProgress);
        let signItem = progressItem(SigningProgressItemKey.SignDocuments, SigningProgressItemStatus.Pending);
        const failedIds = new Set<number>();

        const update = (closable: boolean) => {
            const items = [verdictDone, prepareItem, signItem];
            const retryIds = Array.from(failedIds);
            onSigningProgressChange?.({
                items,
                closable,
                message: closable ? getDocumentsHint(items) : undefined,
                onRetry: closable && retryIds.length > 0
                    ? () => { signDocuments(retryIds, null, thumbprint); }
                    : undefined,
            });
        };

        update(false);

        let preparedDocuments: WorkplaceMedicalCheckupDocumentContentDto[];
        try {
            const prepared = await medicalCheckupSigningService.prepareWorkplaceDocuments(documentIds);
            preparedDocuments = prepared.documents;
            if (prepared.errors.length > 0) {
                console.error("Ошибки формирования направлений к врачу:", prepared.errors);
                prepared.errors.forEach(x => failedIds.add(x.documentId));
                prepareItem = progressItem(
                    SigningProgressItemKey.PrepareDocuments,
                    SigningProgressItemStatus.Error,
                    summarizeErrorMessages(prepared.errors.map(x => x.message), PREPARE_DOCUMENTS_ERROR)
                );
            } else {
                prepareItem = progressItem(SigningProgressItemKey.PrepareDocuments, SigningProgressItemStatus.Done);
            }
        } catch (e: any) {
            console.error(e);
            documentIds.forEach(id => failedIds.add(id));
            prepareItem = progressItem(
                SigningProgressItemKey.PrepareDocuments,
                SigningProgressItemStatus.Error,
                summarizeErrorMessages([getErrorMessage(e)], PREPARE_DOCUMENTS_ERROR)
            );
            update(true);
            return;
        }

        if (preparedDocuments.length === 0) {
            update(true);
            return;
        }

        signItem = progressItem(SigningProgressItemKey.SignDocuments, SigningProgressItemStatus.InProgress);
        update(false);

        try {
            const signed = await medicalCheckupSigningService.signPreparedWorkplaceDocuments(preparedDocuments, thumbprint);
            if (signed.errors.length > 0) {
                console.error("Ошибки подписания направлений к врачу:", signed.errors);
                signed.errors.forEach(x => failedIds.add(x.documentId));
                signItem = progressItem(
                    SigningProgressItemKey.SignDocuments,
                    SigningProgressItemStatus.Error,
                    summarizeErrorMessages(signed.errors.map(x => x.message), SIGN_DOCUMENTS_ERROR)
                );
            } else {
                signItem = progressItem(SigningProgressItemKey.SignDocuments, SigningProgressItemStatus.Done);
            }
        } catch (e: any) {
            console.error(e);
            preparedDocuments.forEach(x => failedIds.add(x.documentId));
            signItem = progressItem(
                SigningProgressItemKey.SignDocuments,
                SigningProgressItemStatus.Error,
                summarizeErrorMessages([getErrorMessage(e)], SIGN_DOCUMENTS_ERROR)
            );
        }

        if (failedIds.size > 0) {
            update(true);
            return;
        }

        update(false);
        finish();
    };

    /**
     * Успешный сценарий: шторка закрывается, медик попадает в «Новые медосмотры».
     */
    const finish = () => {
        onSigningProgressChange?.(null);
        navigate(ROUTES.medicalCheckups.listOfNew);
    };

    const handleVerdict = async (verdictData: MedicalCheckupVerdictDto) => {
        if (!defaultCertificate) {
            // Нет сохраненного сертификата - показываем диалог выбора
            setPendingVerdictData(verdictData);
            setCertificateDialogOpen(true);
            return;
        }

        await performSign(verdictData, defaultCertificate.thumbprint, defaultCertificate);
    };

    const handleCertificateSelected = async (certificate: Certificate) => {
        if (!pendingVerdictData) {
            setCertificateDialogOpen(false);
            return;
        }

        const verdictData = pendingVerdictData;
        setPendingVerdictData(null);
        setCertificateDialogOpen(false);

        await performSign(verdictData, certificate.thumbprint, null);
    };

    const handleAllow = () => {
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Approved,
            rejectReasons: []
        };
        
        // If there are red values or complaints, show confirmation dialog
        if (hasRedValuesOrComplaints()) {
            setOpenAllowConfirmationDialog(true);
        } else {
            handleVerdict(verdictData);
        }
    };

    const handleAllowConfirmed = () => {
        setOpenAllowConfirmationDialog(false);
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Approved,
            rejectReasons: []
        };
        handleVerdict(verdictData);
    };

    const handleNotAllowed = (selectedReasons: number[], comment: string) => {
        setOpenNotAllowedDialog(false);
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Rejected,
            rejectReasons: selectedReasons,
            comment: comment
        };
        handleVerdict(verdictData);
    };

    return (
        <>
            <Box paddingTop={1} px={2} sx={{ display: 'flex', float: 'right' }}>
                <Box px={2}>
                    <DenyButton 
                        onClick={() => setOpenNotAllowedDialog(true)} 
                        disabled={disabled}
                    >
                        Недопуск
                    </DenyButton>
                </Box>
                <Box>
                    <AcceptButton 
                        onClick={handleAllow} 
                        disabled={medicalCheckup.indicators.alcoholParam === MedicalIndicatorsEnum.High || disabled}
                    >
                        Допуск
                    </AcceptButton>
                </Box>
            </Box>
            <NotAllowedDialog
                medicalCheckupId={medicalCheckup.id}
                open={openNotAllowedDialog}
                onSubmit={handleNotAllowed}
                onClose={() => setOpenNotAllowedDialog(false)}
            />
            <ConfirmDialog
                open={openAllowConfirmationDialog}
                title="Подтверждение допуска"
                content="У водителя есть недопустимые значения или жалобы. Вы уверены, что хотите допустить сотрудника?"
                onClose={() => setOpenAllowConfirmationDialog(false)}
                onConfirm={handleAllowConfirmed}
            />
            <CertificateSelectionDialog
                open={certificateDialogOpen}
                onClose={() => {
                    setCertificateDialogOpen(false);
                    setPendingVerdictData(null);
                    onLoadingChange?.(false);
                }}
                onSelect={handleCertificateSelected}
            />
        </>
    );
};

